import React, { useEffect, useState, useCallback } from "react";
import { CurrentClient, CurrentCube, TableVarLogs, User } from "./types";
import { useAuth } from "../layouts/authentication/sign-in/illustration/utils/useAuth";
import { ClientsResponse, useUserLazyQuery, CubesResponse, useCubesLazyQuery } from "graphql/types/graphql";
import { useClientsLazyQuery } from "graphql/types/graphql";
import { useRefreshTokenMutation } from "graphql/types/graphql";
import { CRUXY_ADMIN, CRUXY_USER } from "../common/names";
import { useIdleLogout } from "./useIdleLogout";

export interface ContextData {
  user?: User | undefined;
  setUser: (value: User) => void;
  token: string;
  setToken: (value: string) => void;
  logIn: (email: string, password: string) => Promise<boolean | string>;
  logOut: () => void;
  appLoading: boolean;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  setUpApp: () => void;
  companies: ClientsResponse[];
  selectedCompany: CurrentClient;
  setSelectedCompany: (value: CurrentClient) => void;
  cubes: CubesResponse[];
  selectedCube: CurrentCube;
  setSelectedCube: (value: CurrentCube) => void;
  refreshAccessToken: () => Promise<boolean | string>;
  setShowScroll: (value: boolean) => void;
  showScroll: boolean;
}

export const context = React.createContext<ContextData>({
  user: undefined,
  setUser: () => null,
  token: "",
  setToken: () => null,
  logIn: () => Promise.resolve(""),
  logOut: () => null,
  appLoading: true,
  isLoading: true,
  setIsLoading: () => null,
  setUpApp: () => null,
  companies: [],
  selectedCompany: null,
  setSelectedCompany: () => null,
  cubes: [],
  selectedCube: null,
  setSelectedCube: () => null,
  refreshAccessToken: () => Promise.resolve(false),
  setShowScroll: () => null,
  showScroll: false,
});

export function ContextProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [appLoading, setAppLoading] = useState(true);
  const [identifyByToken] = useUserLazyQuery({ fetchPolicy: "no-cache" });
  const [getCompanies] = useClientsLazyQuery({
    fetchPolicy: "no-cache",
  });

  const [getCubes] = useCubesLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const [getNewAccessToken] = useRefreshTokenMutation({ fetchPolicy: "no-cache", notifyOnNetworkStatusChange: true });

  const [companies, setCompanies] = useState<ClientsResponse[]>([]);
  const [cubes, setCubes] = useState<CubesResponse[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CurrentClient | null>(null);
  const [selectedCube, setSelectedCube] = useState<CurrentCube | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState("");
  const [showScroll, setShowScroll] = useState(false);

  async function fetchAndSetCubes() {
    try {
      const dataCube = await getCubes({
        variables: {
          filter: {
            cubeId: null,
          },
        },
      });
      if (dataCube) {
        const cubes = dataCube?.data?.cubes;
        setCubes(cubes);
        const storedCubeId = localStorage.getItem("currentCube");
        const result = cubes?.find((cube: CubesResponse) => cube.cubeId === storedCubeId);

        if (result) {
          setSelectedCube({
            cubeId: result.cubeId,
            name: result.name,
          });
        } else if (cubes && cubes.length > 0) {
          setSelectedCube({ cubeId: cubes[0].cubeId, name: cubes[0].name });
        } else {
          setSelectedCube(null);
        }
      } else {
        setSelectedCube(null);
      }
    } catch (error) {
      setSelectedCube(null);
    }
  }

  const getUserInfo = async (myToken: string) => {
    const userInfo = await identifyByToken({
      fetchPolicy: "no-cache",
      context: {
        headers: {
          Authorization: myToken,
        },
      },
    });

    if (userInfo) {
      await fetchAndSetCubes();
      if (userInfo.data.me.role === CRUXY_ADMIN || userInfo.data.me.role === CRUXY_USER) {
        const data = await getCompanies({
          variables: {
            filter: {
              cruxyId: null,
            },
          },
          context: {
            headers: {
              Authorization: myToken,
            },
          },
        });
        if (data) {
          const companies = data.data.clients;
          setCompanies(companies);
          const company = localStorage.getItem("currentClient");

          const result = companies?.find((client: ClientsResponse) => {
            return client.cruxyId === company;
          });

          if (result) {
            setSelectedCompany({
              cruxyId: result.cruxyId,
              name: result.name,
            });
            setToken(myToken);
          } else {
            setSelectedCompany({ cruxyId: companies[0].cruxyId, name: companies[0].name });
            setToken(myToken);
          }
        }

        setUser({
          id: userInfo.data.me.id,
          cruxyId: userInfo.data.me.cruxyId,
          role: userInfo.data.me.role,
          azure_sas_token: userInfo.data.me.azure_sas_token,
        });
      } else {
        setUser({
          id: userInfo.data.me.id,
          cruxyId: userInfo.data.me.cruxyId,
          role: userInfo.data.me.role,
          azure_sas_token: userInfo.data.me.azure_sas_token,
        });
        setToken(myToken);
        setSelectedCompany({ cruxyId: userInfo.data.me.cruxyId });
        setAppLoading(false);
      }
    }
  };

  const { user, setUser, logIn, logOut } = useAuth(getCompanies, getCubes, getUserInfo, setToken);

  const { reset } = useIdleLogout(user, logOut);

  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await getNewAccessToken();
      if (response) {
        setToken(response.data.refreshToken);
        reset();
        setIsLoading(false);
        return response.data.refreshToken;
      } else {
        return null;
      }
    } catch (error) {
      setIsLoading(false);
      return false;
    }
  }, [getNewAccessToken, setToken, setIsLoading, reset]);

  useEffect(() => {
    if (selectedCube) {
      localStorage.setItem("currentCube", selectedCube.cubeId.toString());
    }
  }, [selectedCube]);

  useEffect(() => {
    if (user && token && (selectedCube || selectedCube === null)) {
      setAppLoading(false);
    }
  }, [user, token, selectedCube]);

  useEffect(() => {
    if (!user) {
      setToken("");
    }
  }, [user, token, selectedCube]);

  /**
   * Sets up an interval to refresh the authentication token every 9 minutes
   * for the Tableau embed component, which requires a fresh token due to
   * its 10-minute token expiry policy. The interval is cleared when the
   * component unmounts to prevent memory leaks.
   */
  useEffect(() => {
    if (token) {
      const interval = setInterval(async () => {
        const newToken = await refreshAccessToken();
        if (newToken && typeof newToken === "string") {
          setToken(newToken);
          reset();
        }
      }, 540000);

      return () => clearInterval(interval);
    }
  }, [token, refreshAccessToken, reset]);

  async function setUpApp() {
    try {
      setAppLoading(true);
      const newToken = await getNewAccessToken();
      if (newToken) {
        getUserInfo(newToken.data.refreshToken);
        reset();
      } else {
        setAppLoading(false);
        setIsLoading(false);
      }
    } catch (error) {
      setAppLoading(false);
      setIsLoading(false);
    }
  }

  return (
    <context.Provider
      value={{
        user,
        setUser,
        token,
        setToken,
        logIn,
        logOut,
        appLoading,
        setUpApp,
        companies,
        selectedCompany,
        setSelectedCompany,
        cubes,
        selectedCube,
        setSelectedCube,
        isLoading,
        setIsLoading,
        refreshAccessToken,
        showScroll,
        setShowScroll,
      }}
    >
      {children}
    </context.Provider>
  );
}
